import moment from 'moment';
import {
  DeliveryInfo,
  DeliveryInfoDeliveryMoment,
  DeliveryInfoDish
} from '../../services/website/types';
import {
  DishFormValue,
  FormProductValuesSap,
  FormValues,
  FormValuesSap,
  SelectableDeliveryMoment,
  ValidationResult
} from './types';
import {
  BezorgmomentResponse,
  CartUpsertRequest,
  CustomerInformationForBezorgmomentenResponse
} from '../../services/deliverer/types';
import { NanoIdGenerator } from '../../../helpers/NanoIdGenerator';

export const hasDeliveryMomentExpired = (deliveryMoment: DeliveryInfoDeliveryMoment) => {
  const diffInDays = moment(deliveryMoment.closing_time).diff(moment(), 'days');

  return diffInDays < 3;
};

export const findDeliveryInfoByDeliveryId = (
  deliveryInfos: ReadonlyArray<DeliveryInfo>,
  deliveryId: number
): DeliveryInfo | null => {
  return deliveryInfos.find((deliveryInfo) => deliveryInfo.deliveryId === deliveryId) || null;
};

export const findDeliveryMoment = (
  deliveryMoments: ReadonlyArray<DeliveryInfoDeliveryMoment>,
  deliveryMomentId: number
): DeliveryInfoDeliveryMoment | null => {
  return (
    deliveryMoments.find(
      (deliveryMoment) => deliveryMoment.deliveryMomentId === deliveryMomentId
    ) || null
  );
};

export const getSelectedDeliveryMoment = (
  selectableDeliveryMoments: ReadonlyArray<SelectableDeliveryMoment>,
  selectedDeliveryId: number,
  selectedDeliveryMomentId: number
): SelectableDeliveryMoment | null => {
  return (
    selectableDeliveryMoments.find(
      (selectableDeliveryMoment) =>
        selectableDeliveryMoment.deliveryId === selectedDeliveryId &&
        selectableDeliveryMoment.deliveryMomentId === selectedDeliveryMomentId
    ) || null
  );
};

const MAALTIJD_TYPE_MAALTIJDSERVICE = 'maaltijdservice';
const MAALTIJD_TYPE_MAALTIJDPAKKETTEN = 'maaltijdpakket';

const isCamasysMealAMealbox = (matchingDish: DeliveryInfoDish) =>
  matchingDish &&
  matchingDish.type &&
  matchingDish.type.toLowerCase() === MAALTIJD_TYPE_MAALTIJDPAKKETTEN;

export const getMainMealCountForDishSingle = (matchingDish: DeliveryInfoDish, quantity: number) => {
  const acceptedList = [MAALTIJD_TYPE_MAALTIJDSERVICE, MAALTIJD_TYPE_MAALTIJDPAKKETTEN];

  if (acceptedList.includes(matchingDish.type.toLowerCase())) {
    if (isCamasysMealAMealbox(matchingDish)) {
      if (matchingDish.ingredients && matchingDish.ingredients.length > 0) {
        const mainMealIngredients = matchingDish.ingredients.filter(
          //ingredients can be submeals in mealboxes if type = maaltijdservice
          (ingredient) => ingredient.type.toLowerCase() === MAALTIJD_TYPE_MAALTIJDSERVICE
        );
        if (mainMealIngredients.length === 0) {
          return quantity * 5;
        }
        return quantity * mainMealIngredients.length;
      } else {
        return quantity * 5;
      }
    } else {
      return quantity;
    }
  }

  return 0;
};

export const getMainMealCountForDish = (
  matchingDish: DeliveryInfoDish,
  portionFormValues: { quantity: number }[]
) => {
  return portionFormValues.reduce((mainMealCount, portionFormValue) => {
    return mainMealCount + getMainMealCountForDishSingle(matchingDish, portionFormValue.quantity);
  }, 0);
};

const getMainMealCount = (deliveryInfo: DeliveryInfo, dishFormValues: DishFormValue[]): number => {
  return dishFormValues.reduce((totalMainMealCount, dishFormValue) => {
    const matchingDish = deliveryInfo.dishes.find(
      (dish) => dish.camasys_id === dishFormValue.camasysid
    );

    if (!matchingDish) {
      return totalMainMealCount;
    }

    const portions = Object.values(dishFormValue.portions).filter(
      (portion) => !isNaN(portion.quantity)
    );
    const dishMainMealCount = getMainMealCountForDish(matchingDish, portions);

    return totalMainMealCount + dishMainMealCount;
  }, 0);
};

export const getPlaceOrderValidationResult = (
  deliveryInfo: DeliveryInfo | null,
  values: FormValues
): ValidationResult => {
  if (!deliveryInfo) {
    return {
      isValid: false,
      message: 'Er is geen bezorgmoment geselecteerd'
    };
  }

  const dishes = Object.values(values.dishes);
  const selectedDishes = dishes.filter((dish) => dish.selected);

  if (selectedDishes.length === 0) {
    return {
      isValid: false,
      message: 'Er zijn geen maaltijden gekozen'
    };
  }

  const selectedDishesWithoutPortions = selectedDishes.filter((selectedDish) => {
    const portions = Object.values(selectedDish.portions);
    return !portions.some((portion) => portion.quantity > 0);
  });

  if (selectedDishesWithoutPortions.length > 0) {
    return {
      isValid: false,
      message: 'Er is een maaltijd gekozen waarvoor geen aantal is ingevoerd'
    };
  }

  const mainMealCount = getMainMealCount(deliveryInfo, selectedDishes);

  if (mainMealCount < 2) {
    return {
      isValid: false,
      message: 'Er moeten minimaal 2 maaltijden besteld worden op een bezorgmoment.'
    };
  }

  return { isValid: true };
};

export const getUpsertCartRequest = (
  customerInformation: CustomerInformationForBezorgmomentenResponse,
  bezorgmoment: BezorgmomentResponse,
  cartId: string | null,
  values: FormValuesSap
): CartUpsertRequest => {
  const distributionChannel = '10';
  const salesOrganization = '3710';
  const source = 'bezorgersweb';

  return {
    cartId: cartId ?? NanoIdGenerator.generateReadableId(),
    customerNumber: customerInformation.customerNumber,
    distributionChannel: distributionChannel,
    salesOrganization: salesOrganization,
    source: source,
    deliveries: [
      {
        deliveryId: '001', // We can (currently) only send 1 delivery at a time
        suggestedDeliveryMoment: bezorgmoment.startDeliveryMoment,
        pickMoment: bezorgmoment.pickMoment,
        comment: values.route_comments,
        address: null,
        cartItems: [
          // Add main menu items
          ...Object.values(values.weeklyMenuItems || [])
            .filter((menuItem) => menuItem.selected)
            .flatMap((menuItem) =>
              Object.values(menuItem.portions)
                .filter((portion) => portion.quantity > 0)
                .map((portion) => ({
                  productId: portion.productId,
                  quantity: portion.quantity
                }))
            ),
          // Add alternative items
          ...Object.values(values.alternativeItems || {}).flatMap((alternativeGroup) =>
            Object.values(alternativeGroup)
              .filter((alternative) => alternative.selected)
              .flatMap((alternative) => {
                // Check if it's a box alternative with portions
                if (alternative.portions) {
                  return Object.values(alternative.portions)
                    .filter((portion) => portion.quantity > 0)
                    .map((portion) => ({
                      productId: portion.productId,
                      quantity: portion.quantity
                    }));
                }
                // Regular alternative with single quantity
                if (alternative.quantity && alternative.quantity > 0) {
                  return [
                    {
                      productId: alternative.productId,
                      quantity: alternative.quantity
                    }
                  ];
                }
                return [];
              })
          )
        ]
      }
    ],
    couponCodes: values.voucher_code ? [String(values.voucher_code)] : []
  };
};

export const hasMealWithMoreThanThreeDishesForSap = (mealItems: FormProductValuesSap[]) => {
  return mealItems.some(
    (item) => item.portions && Object.values(item.portions).some((meal) => meal.quantity > 3)
  );
};

export const hasOrderValidAmountOfProducts = (mealItems: FormProductValuesSap[]) => {
  // Count MTB and MTD category items
  let mtbCount = 0;
  let mtdCount = 0;
  mealItems.forEach((item) => {
    const totalItemQuantity = Object.values(item.portions || {}).reduce(
      (portionSum, portion) => portionSum + (portion.quantity || 0),
      0
    );
    if (item.category === 'MTB') {
      mtbCount += totalItemQuantity;
    } else if (item.category === 'MTD') {
      mtdCount += totalItemQuantity;
    }
  });
  // Return true if at least one MTB or at least two MTD
  return mtbCount >= 1 || mtdCount >= 2;
};
